import type { ResizableDrawerProps } from '@unifyapps/carbon/components/ResizableDrawer';
import { DATA_NAV_POSITION_ATTRIBUTE } from '@unifyapps/carbon/no-code/components/CustomNavigation/CustomNavigation';
import InterfaceHelper from '@unifyapps/carbon/no-code/stores/InterfaceStore/helper/InterfaceHelper';
import { getResolutionByDevice } from '@unifyapps/carbon/no-code/utils/getResolutionByDevice';
import { NavigationPosition } from '@unifyapps/defs/types/interface';
import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { findKey } from 'lodash';
import { useCallback } from 'react';
import { NotificationsHelper } from '../../../../../helpers/NotificationsHelper';
import { NOTIFICATION_PAGE_SLUG } from '../../../../../notifications/consts';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useAppDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import { useDrawerRefContext } from '../../../../context/DrawerRefProvider';
import {
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../../../../stores/app/GlobalAppStateStore';
import { InterfaceModes } from '../../../../stores/InterfaceStore';
import { ComputeContextKeys } from '../../../computeContext/const';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import { useNavigateToPage } from '../navigateToPage';

export type Operation = 'openNotificationPage' | 'open' | 'close' | 'toggle';

/**
 * In case of custom side navigation, when we have expandable side nav, and want
 * to open notification panel, we dont know the configured expanded width of the side nav. Since
 * the element with expnadable width may be absolutely positioned and we dont know the exact width
 * of that element, need to traverse complete custom navigation children DOM to find the max width.
 */
export const getStartOffset = (pageEl: HTMLElement | null) => {
  if (!pageEl) return 0;

  function getMaxDescendantWidth(parentEl: HTMLElement) {
    const allChildren = parentEl.querySelectorAll('*');

    let maxWidth = 0;

    allChildren.forEach((el) => {
      const { width } = el.getBoundingClientRect();
      if (width > maxWidth) {
        maxWidth = width;
      }
    });

    return maxWidth;
  }

  const maxWidth = getMaxDescendantWidth(pageEl);
  return maxWidth;
};

/**
 * For custom Top Navigation, we need to calculate the top offset of the notification panel.
 * We can direclty get the height using top navbar container + top navigation bar height if absolutely positioned.
 */
export const getTopOffset = (pageEl: HTMLElement | null) => {
  if (!pageEl) return 0;

  const { height } = pageEl.getBoundingClientRect();

  return height;
};

/**
 * Drawer positioning
 * Side Navigation : PageEl will have refrence to custom navigation container, calculate
 *                   the start offset of the drawer by traversing the custom navigation children DOM to find the max width.
 * Top Navigation  : PageEl will have refrence to custom top navigation container, calculate the top offset of the drawer by getting
 *                   the height of the top navigation container.In builder mode, the by default fixed positioning of drawer breaks, so need
 *                   to get reference to canvas content container (interface-page-container) and use it as anchor and absolutely position
 *                   the drawer.
 */
export function useControlNotification() {
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const mode = useGlobalAppStateStore().hooks.useMode();
  const deviceDetails = useGlobalAppStateStore().hooks.useDeviceDetails();
  const resolution = getResolutionByDevice(deviceDetails.device, mode);
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['interfaces-matrix']);
  const drawerRef = useDrawerRefContext();
  const {
    actions: { notifyDependencyChange },
  } = useAppDependencyGraphProvider();

  const { navigateToPage } = useNavigateToPage();

  const notificationPageId = findKey(
    InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord),
    (slug) => slug === NOTIFICATION_PAGE_SLUG,
  );

  const basePageEl = useContainerElementContext();

  const onControlNotification: ActionHandlerType = useCallback(
    (args: OnActionArgs) => {
      const { action } = args;
      const operation = action?.payload?.operation ?? 'open';

      const hidePanel = drawerRef?.current?.hideDrawer;

      const { navigation, actions } = getGlobalAppStateStoreState();
      const { isNotificationPanelOpen } = navigation;

      if (mode === InterfaceModes.BUILDER) {
        showSnackbar({
          color: 'warning',
          title: t('interfaces-matrix:appActions.ControlNotification'),
        });
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      if (operation === 'openNotificationPage') {
        if (notificationPageId) {
          return navigateToPage({
            ...args,
            action: {
              payload: {
                pageId: notificationPageId,
                history: 'push',
                target: '_self',
              },
            },
          });
        }
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      if (operation === 'close') {
        if (isNotificationPanelOpen) {
          hidePanel?.();
          actions.setNavigationState({ isNotificationPanelOpen: false });
          notifyDependencyChange({
            id: ComputeContextKeys.INTERFACE_RECORD,
          });
        }
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      if (operation === 'toggle') {
        if (isNotificationPanelOpen) {
          hidePanel?.();
          actions.setNavigationState({ isNotificationPanelOpen: false });
          notifyDependencyChange({
            id: ComputeContextKeys.INTERFACE_RECORD,
          });
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
        }
      }
      // resolution is the current navigation breakpoint (xl | lg | md | sm); use for resolution-specific drawer styles or to skip panel on SM
      const navPosition = basePageEl?.getAttribute(DATA_NAV_POSITION_ATTRIBUTE) as
        | NavigationPosition
        | undefined;

      const isTopPositioned = navPosition === NavigationPosition.Top;

      const topOffset = isTopPositioned ? getTopOffset(basePageEl) : 0;
      const leftOffset = navPosition === NavigationPosition.Start ? getStartOffset(basePageEl) : 0;

      const drawerStartOffset = parseInt(String(leftOffset ?? 0), 10);

      const container =
        isTopPositioned && mode !== InterfaceModes.RUNNER
          ? document.querySelector('.interface-page-container')
          : basePageEl;

      const height = container?.getBoundingClientRect().height;

      const drawerProps = NotificationsHelper.getDrawerStylesForCustomNavigation({
        isTopPositioned,
        drawerStartOffset,
        extraTopOffset: mode !== InterfaceModes.RUNNER && !isTopPositioned ? 56 : 0,
        height: topOffset ?? 0,
        position: navPosition,
        drawerHeight: height,
        mode,
      });

      const slotProps = NotificationsHelper.getNotificationDrawerSlotProps(
        drawerProps,
        isTopPositioned,
      );

      const anchor = (isTopPositioned ? 'right' : 'left') as ResizableDrawerProps['anchor'];
      const showPanel = drawerRef?.current?.showDrawer;

      if (showPanel && !isNotificationPanelOpen) {
        showPanel({
          anchor,
          template: 'notifications',
          container,
          slotProps,
          componentProps: {
            notificationTags: interfaceRecord.properties.notifications?.tags,
            resolution,
          },
          callbacks: {
            onShow: () => {
              actions.setNavigationState({ isNotificationPanelOpen: true });
              notifyDependencyChange({
                id: ComputeContextKeys.INTERFACE_RECORD,
              });
            },
            onHide: () => {
              actions.setNavigationState({ isNotificationPanelOpen: false });
              notifyDependencyChange({
                id: ComputeContextKeys.INTERFACE_RECORD,
              });
            },
          },
        });
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [
      getGlobalAppStateStoreState,
      mode,
      interfaceRecord,
      t,
      showSnackbar,
      drawerRef,
      basePageEl,
      resolution,
      notifyDependencyChange,
      navigateToPage,
      notificationPageId,
    ],
  );

  return {
    onControlNotification,
  };
}
